import React, { createContext, useContext, useState, useEffect } from 'react';
import authService, { User, LoginCredentials } from '../services/auth';
import API from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  // Merge changes into the signed-in user (and the cached copy) without a re-login.
  updateUser: (patch: Partial<User>) => void;
  // Start a session from a login/MFA response.
  setSession: (user: User, token: string) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Hydrate from the cache first so the app paints, then refresh from the server so a role
    // change, deactivation or renamed account takes effect without a re-login. A failed refresh
    // keeps the cached copy; a 401 is handled by the API client.
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    }
    setLoading(false);
    if (currentUser && localStorage.getItem('token')) {
      API.get('/api/auth/me')
        .then((r) => {
          const fresh = r.data?.user;
          if (!fresh) return;
          setUser((prev) => {
            const next = { ...(prev || {}), ...fresh } as User;
            try { localStorage.setItem('user', JSON.stringify(next)); } catch { /* storage unavailable */ }
            return next;
          });
        })
        .catch(() => { /* offline or transient: keep the cached user */ });
    }
  }, []);

  const setSession = (u: User, token: string) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(u));
    setUser(u);
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const updateUser = (patch: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      try { localStorage.setItem('user', JSON.stringify(next)); } catch { /* storage unavailable */ }
      return next;
    });
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        updateUser,
        setSession,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};